/**
 * DocumentUpload Component
 * 
 * Componente para upload de documentos com drag & drop, preview e validação.
 * 
 * Funcionalidades:
 * - Drag & drop de arquivos
 * - Preview de arquivo selecionado
 * - Validação de tipo e tamanho
 * - Indicador de progresso
 * - Feedback visual
 */

import { useState, useRef, useCallback } from 'react'
import {
  Upload,
  Image as ImageIcon,
  FileText,
  FileEdit,
  Paperclip,
  AlertTriangle,
  X
} from 'lucide-react'
import './DocumentUpload.css'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
]
const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'doc', 'docx']

function DocumentUpload({
  onFileSelect,
  onFileRemove,
  selectedFile = null,
  disabled = false,
  className = '',
  accept = ALLOWED_TYPES.join(','),
  maxSize = MAX_FILE_SIZE
}) {
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState(null)
  const fileInputRef = useRef(null)

  const validateFile = useCallback((file) => {
    // Validar tipo
    const isValidType = ALLOWED_TYPES.includes(file.type) ||
      ALLOWED_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(`.${ext}`))
    
    if (!isValidType) {
      setError('Tipo de arquivo não permitido. Tipos permitidos: PDF, imagens (JPG, PNG, GIF), documentos (DOC, DOCX)')
      return false
    }

    // Validar tamanho
    if (file.size > maxSize) {
      setError(`Arquivo muito grande. Tamanho máximo: ${(maxSize / 1024 / 1024).toFixed(0)}MB`)
      return false
    }

    setError(null)
    return true
  }, [maxSize])

  const handleFileChange = useCallback((file) => {
    if (!file) return

    if (validateFile(file)) {
      onFileSelect?.(file)
    }
  }, [validateFile, onFileSelect])

  const handleInputChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileChange(file)
    }
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (disabled) return

    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileChange(file)
    }
  }

  const handleRemove = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    setError(null)
    onFileRemove?.()
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase()
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
      return <ImageIcon size={32} />
    }
    if (ext === 'pdf') {
      return <FileText size={32} />
    }
    if (['doc', 'docx'].includes(ext)) {
      return <FileEdit size={32} />
    }
    return <Paperclip size={32} />
  }

  return (
    <div className={`document-upload ${className}`}>
      {!selectedFile ? (
        <div
          className={`document-upload-dropzone ${isDragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''} ${error ? 'error' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleInputChange}
            disabled={disabled}
            style={{ display: 'none' }}
          />
          
          <div className="document-upload-content">
            <div className="document-upload-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
              <Upload size={48} />
            </div>
            <p className="document-upload-text">
              <strong>Arraste e solte</strong> um arquivo aqui ou <strong>clique para selecionar</strong>
            </p>
            <p className="document-upload-hint">
              Formatos: PDF, JPG, PNG, GIF, DOC, DOCX • Máximo: {(maxSize / 1024 / 1024).toFixed(0)}MB
            </p>
          </div>
        </div>
      ) : (
        <div className="document-upload-preview">
          <div className="document-upload-preview-content">
            <div className="document-upload-preview-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
              {getFileIcon(selectedFile.name)}
            </div>
            <div className="document-upload-preview-info">
              <p className="document-upload-preview-name">{selectedFile.name}</p>
              <p className="document-upload-preview-size">{formatFileSize(selectedFile.size)}</p>
            </div>
            {!disabled && (
              <button
                type="button"
                className="document-upload-remove"
                onClick={handleRemove}
                aria-label="Remover arquivo"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={20} />
              </button>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="document-upload-error" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <AlertTriangle size={16} />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}

export default DocumentUpload

